import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, RefreshCw, PlayCircle, Eye, X } from 'lucide-react';

const REJECTION_REASONS = [
  'Startup', 'ShortFill', 'Undercut', 'SilverStreaks', 'Burn Mark',
  'Flowline', 'Shinemark', 'Weldline', 'Scratches', 'Bubbles',
  'Sink mark', 'Color Variation', 'Others',
];

const STATUS_COLORS = {
  DRAFT: 'bg-slate-100 text-slate-600',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

const emptyOutput = (partId) => ({
  part_id: partId,
  produced_quantity: 0,
  accepted_quantity: 0,
  rejected_quantity: 0,
  rejection_weight: 0,
  rejections: [],
});

const emptyRejection = () => ({
  rejected_quantity: 0,
  rejection_weight: 0,
  reason_for_rejection: '',
});

const ProductionRun = () => {
  const { api, isAdmin, hasRole } = useAuth();
  const [data, setData] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [moulds, setMoulds] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [grnList, setGrnList] = useState([]);
  const [parts, setParts] = useState([]);
  const [items, setItems] = useState([]);
  const [grnCache, setGrnCache] = useState({});
  const [issueCache, setIssueCache] = useState({});
  const fetchingGrns = useRef(new Set());
  const fetchingIssues = useRef(new Set());

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingRun, setViewingRun] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [formData, setFormData] = useState({
    template_id: '', customer_id: '', machine_id: '',
    run_date: new Date().toISOString().split('T')[0],
    shift_id: '', shift_incharge_id: '', operator_id: '',
    no_of_shots: 0, lumps: 0, runner_weight_total: 0,
    status: 'DRAFT', notes: '',
  });
  const [outputs, setOutputs] = useState([]);
  const [materialConsumptions, setMaterialConsumptions] = useState([]);

  const canEdit = isAdmin() || hasRole(['ADMIN', 'PRODUCTION']);

  const fetchGrnsForItem = useCallback(async (itemId) => {
    if (!itemId || fetchingGrns.current.has(itemId)) return;
    fetchingGrns.current.add(itemId);
    try {
      const r = await api.get(`/grn-by-material/${itemId}`);
      setGrnCache(prev => ({ ...prev, [itemId]: r.data }));
    } catch {}
  }, [api]);

  const fetchIssuesForGrn = useCallback(async (purchaseId, itemId) => {
    if (!purchaseId || !itemId) return;
    const key = `${purchaseId}_${itemId}`;
    if (fetchingIssues.current.has(key)) return;
    fetchingIssues.current.add(key);
    try {
      const r = await api.get(`/issue-items-by-grn?purchase_id=${purchaseId}&item_id=${itemId}`);
      setIssueCache(prev => ({ ...prev, [key]: r.data }));
    } catch {}
  }, [api]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    fetchingGrns.current.clear();
    fetchingIssues.current.clear();
    setGrnCache({});
    setIssueCache({});
    try {
      const res = await api.get('/production-runs');
      setData(res.data);
    } catch {
      toast.error('Failed to fetch production runs');
    } finally {
      setLoading(false);
    }
    try { const r = await api.get('/production-templates'); setTemplates(r.data.filter(t => t.is_active && t.status === 'ACTIVE')); } catch (e) {}
    try { const r = await api.get('/customers'); setCustomers(r.data.filter(c => c.is_active)); } catch (e) {}
    try { const r = await api.get('/machines'); setMachines(r.data.filter(m => m.is_active)); } catch (e) {}
    try { const r = await api.get('/moulds'); setMoulds(r.data.filter(m => m.is_active)); } catch (e) {}
    try { const r = await api.get('/shifts'); setShifts(r.data.filter(s => s.is_active)); } catch (e) {}
    try { const r = await api.get('/employees'); setEmployees(r.data.filter(e => e.is_active)); } catch (e) {}
    try { const r = await api.get('/grn-available'); setGrnList(r.data); } catch (e) {}
    try { const r = await api.get('/parts'); setParts(r.data); } catch (e) {}
    try { const r = await api.get('/items'); setItems(r.data.filter(i => i.is_active)); } catch (e) {}
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getLabel = (list, id, keyFn) => {
    const item = list.find(x => x.id === parseInt(id));
    return item ? keyFn(item) : '-';
  };
  const getTemplateName = (id) => getLabel(templates, id, t => `${t.template_no} - ${t.name}`);
  const getCustomerName = (id) => getLabel(customers, id, c => `${c.code} - ${c.name}`);
  const getMachineName = (id) => getLabel(machines, id, m => `${m.code} - ${m.name}`);
  const getShiftName = (id) => getLabel(shifts, id, s => s.name);
  const getEmpName = (id) => getLabel(employees, id, e => `${e.employee_code} - ${e.first_name} ${e.last_name || ''}`);
  const getPartLabel = (id) => getLabel(parts, id, p => `${p.part_no} - ${p.name}`);
  const getPartWeight = (id) => {
    const p = parts.find(x => x.id === parseInt(id));
    return p ? parseFloat(p.part_weight || 0) : 0;
  };
  const getMouldRunnerWeight = (mouldId) => {
    const m = moulds.find(x => x.id === parseInt(mouldId));
    return m ? parseFloat(m.runner_weight || 0) : 0;
  };
  const getGRNLabel = (id) => {
    if (!id) return '-';
    const g = grnList.find(x => x.id === parseInt(id));
    return g ? `GRN-${g.grn_no}` : `GRN-${id}`;
  };

  const templateMaterialItems = selectedTemplate
    ? items.filter(it => (selectedTemplate.materials || []).some(m => m.item_id === it.id))
    : items;

  // ── Output helpers ──────────────────────────────────────────────
  const recalcOutput = (output) => {
    const totalRejected = output.rejections.reduce((s, r) => s + (parseFloat(r.rejected_quantity) || 0), 0);
    const totalRejWeight = output.rejections.reduce((s, r) => s + (parseFloat(r.rejection_weight) || 0), 0);
    return {
      ...output,
      rejected_quantity: totalRejected,
      rejection_weight: parseFloat(totalRejWeight.toFixed(6)),
      accepted_quantity: Math.max(0, (parseFloat(output.produced_quantity) || 0) - totalRejected),
    };
  };

  const handleProducedChange = (i, value) => {
    setOutputs(prev => {
      const updated = [...prev];
      updated[i] = recalcOutput({ ...updated[i], produced_quantity: value });
      return updated;
    });
  };

  const addRejection = (i) => {
    setOutputs(prev => {
      const updated = [...prev];
      updated[i] = recalcOutput({ ...updated[i], rejections: [...updated[i].rejections, emptyRejection()] });
      return updated;
    });
  };

  const removeRejection = (i, j) => {
    setOutputs(prev => {
      const updated = [...prev];
      const newRejections = updated[i].rejections.filter((_, idx) => idx !== j);
      updated[i] = recalcOutput({ ...updated[i], rejections: newRejections });
      return updated;
    });
  };

  const updateRejection = (i, j, field, value) => {
    setOutputs(prev => {
      const updated = [...prev];
      const newRejections = [...updated[i].rejections];
      newRejections[j] = { ...newRejections[j], [field]: value };
      if (field === 'rejected_quantity') {
        const qty = parseFloat(value) || 0;
        newRejections[j].rejection_weight = parseFloat((qty * getPartWeight(updated[i].part_id)).toFixed(6));
      }
      updated[i] = recalcOutput({ ...updated[i], rejections: newRejections });
      return updated;
    });
  };

  // ── Material helpers ─────────────────────────────────────────────
  const addMaterialRow = () => setMaterialConsumptions(prev => [
    ...prev, { item_id: '', purchase_id: '', issue_item_no: '', quantity_consumed: 0, uom: 'kg' },
  ]);
  const removeMaterialRow = (i) => setMaterialConsumptions(prev => prev.filter((_, idx) => idx !== i));

  const updateMaterial = (i, field, value) => {
    const currentRow = materialConsumptions[i];
    setMaterialConsumptions(prev => {
      const updated = [...prev];
      if (field === 'item_id') {
        updated[i] = { ...updated[i], item_id: value, purchase_id: '', issue_item_no: '' };
      } else if (field === 'purchase_id') {
        updated[i] = { ...updated[i], purchase_id: value, issue_item_no: '' };
      } else {
        updated[i] = { ...updated[i], [field]: value };
      }
      return updated;
    });
    if (field === 'item_id' && value) fetchGrnsForItem(value);
    else if (field === 'purchase_id' && value && currentRow?.item_id) fetchIssuesForGrn(value, currentRow.item_id);
  };

  // ── Template / Shots handlers ────────────────────────────────────
  const handleTemplateChange = (templateId) => {
    const tmpl = templates.find(t => t.id === parseInt(templateId));
    setSelectedTemplate(tmpl || null);
    setFormData(prev => ({ ...prev, template_id: templateId, runner_weight_total: 0 }));
    if (tmpl) {
      setOutputs((tmpl.parts || []).map(p => emptyOutput(p.part_id.toString())));
      const consumptions = (tmpl.materials || []).map(m => ({
        item_id: m.item_id.toString(), purchase_id: '', issue_item_no: '',
        quantity_consumed: 0, uom: 'kg',
      }));
      setMaterialConsumptions(consumptions);
      consumptions.forEach(mc => { if (mc.item_id) fetchGrnsForItem(mc.item_id); });
    } else {
      setOutputs([]);
      setMaterialConsumptions([]);
    }
  };

  const handleShotsChange = (shots) => {
    const numShots = parseInt(shots) || 0;
    const mouldId = selectedTemplate?.mould_id;
    const runnerWeightPerShot = mouldId ? getMouldRunnerWeight(mouldId) : 0;
    setFormData(prev => ({
      ...prev,
      no_of_shots: numShots,
      runner_weight_total: parseFloat((numShots * runnerWeightPerShot).toFixed(4)),
    }));
  };

  // ── Dialog open ──────────────────────────────────────────────────
  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      const tmpl = templates.find(t => t.id === item.template_id) || null;
      setSelectedTemplate(tmpl);
      setFormData({
        template_id: item.template_id ? item.template_id.toString() : '',
        customer_id: item.customer_id ? item.customer_id.toString() : '',
        machine_id: item.machine_id ? item.machine_id.toString() : '',
        run_date: item.run_date || new Date().toISOString().split('T')[0],
        shift_id: item.shift_id ? item.shift_id.toString() : '',
        shift_incharge_id: item.shift_incharge_id ? item.shift_incharge_id.toString() : '',
        operator_id: item.operator_id ? item.operator_id.toString() : '',
        no_of_shots: item.no_of_shots || 0,
        lumps: item.lumps || 0,
        runner_weight_total: item.runner_weight_total || 0,
        status: item.status || 'DRAFT',
        notes: item.notes || '',
      });
      setOutputs((item.outputs || []).map(o => ({
        part_id: o.part_id.toString(),
        produced_quantity: o.produced_quantity || 0,
        accepted_quantity: o.accepted_quantity || 0,
        rejected_quantity: o.rejected_quantity || 0,
        rejection_weight: o.rejection_weight || 0,
        rejections: (o.rejections || []).map(r => ({
          rejected_quantity: r.rejected_quantity || 0,
          rejection_weight: r.rejection_weight || 0,
          reason_for_rejection: r.reason_for_rejection || '',
        })),
      })));
      const consumptions = (item.material_consumptions || []).map(mc => ({
        item_id: mc.item_id.toString(),
        purchase_id: mc.purchase_id ? mc.purchase_id.toString() : '',
        issue_item_no: mc.issue_item_no || '',
        quantity_consumed: mc.quantity_consumed || 0,
        uom: mc.uom || 'kg',
      }));
      setMaterialConsumptions(consumptions);
      consumptions.forEach(mc => {
        if (mc.item_id) fetchGrnsForItem(mc.item_id);
        if (mc.item_id && mc.purchase_id) fetchIssuesForGrn(mc.purchase_id, mc.item_id);
      });
    } else {
      setEditingItem(null);
      setSelectedTemplate(null);
      setFormData({
        template_id: '', customer_id: '', machine_id: '',
        run_date: new Date().toISOString().split('T')[0],
        shift_id: '', shift_incharge_id: '', operator_id: '',
        no_of_shots: 0, lumps: 0, runner_weight_total: 0,
        status: 'DRAFT', notes: '',
      });
      setOutputs([]);
      setMaterialConsumptions([]);
    }
    setIsDialogOpen(true);
  };

  // ── Submit ───────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.template_id) { toast.error('Template is required'); return; }
    if (!formData.run_date) { toast.error('Run date is required'); return; }

    setIsSubmitting(true);
    try {
      const payload = {
        template_id: parseInt(formData.template_id),
        customer_id: formData.customer_id ? parseInt(formData.customer_id) : null,
        machine_id: formData.machine_id ? parseInt(formData.machine_id) : null,
        run_date: formData.run_date,
        shift_id: formData.shift_id ? parseInt(formData.shift_id) : null,
        shift_incharge_id: formData.shift_incharge_id ? parseInt(formData.shift_incharge_id) : null,
        operator_id: formData.operator_id ? parseInt(formData.operator_id) : null,
        no_of_shots: parseInt(formData.no_of_shots) || 0,
        lumps: parseFloat(formData.lumps) || 0,
        runner_weight_total: parseFloat(formData.runner_weight_total) || 0,
        status: formData.status,
        notes: formData.notes || null,
        outputs: outputs.filter(o => o.part_id).map(o => ({
          part_id: parseInt(o.part_id),
          produced_quantity: parseFloat(o.produced_quantity) || 0,
          accepted_quantity: parseFloat(o.accepted_quantity) || 0,
          rejected_quantity: parseFloat(o.rejected_quantity) || 0,
          rejection_weight: parseFloat(o.rejection_weight) || 0,
          rejections: o.rejections.filter(r => parseFloat(r.rejected_quantity) > 0).map(r => ({
            rejected_quantity: parseFloat(r.rejected_quantity) || 0,
            rejection_weight: parseFloat(r.rejection_weight) || 0,
            reason_for_rejection: r.reason_for_rejection || null,
          })),
        })),
        material_consumptions: materialConsumptions.filter(mc => mc.item_id).map(mc => ({
          item_id: parseInt(mc.item_id),
          purchase_id: mc.purchase_id ? parseInt(mc.purchase_id) : null,
          issue_item_no: mc.issue_item_no || null,
          quantity_consumed: parseFloat(mc.quantity_consumed) || 0,
          uom: mc.uom || null,
        })),
      };

      if (editingItem) {
        await api.put(`/production-runs/${editingItem.id}`, payload);
        toast.success('Production run updated');
      } else {
        await api.post('/production-runs', payload);
        toast.success('Production run created');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete run RUN-${item.run_no}?`)) return;
    try {
      await api.delete(`/production-runs/${item.id}`);
      toast.success('Run deleted');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Delete failed');
    }
  };

  const handleView = async (item) => {
    try {
      const res = await api.get(`/production-runs/${item.id}`);
      setViewingRun(res.data);
      setIsViewDialogOpen(true);
    } catch { toast.error('Failed to load run details'); }
  };

  const filtered = data.filter(d => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (d.run_no ? `RUN-${d.run_no}` : '').toLowerCase().includes(t) ||
      getTemplateName(d.template_id).toLowerCase().includes(t);
  });

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <PlayCircle className="w-6 h-6 text-sky-500" /> Production Runs
          </h1>
          <p className="text-slate-500 text-sm">{data.length} runs</p>
        </div>
        {canEdit && (
          <Button onClick={() => handleOpenDialog()} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
            <Plus className="w-4 h-4 mr-2" /> New Run
          </Button>
        )}
      </div>

      <Card className="mb-4 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search run no, template..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 rounded-sm" />
            </div>
            <Button variant="outline" size="icon" onClick={fetchData} className="rounded-sm">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No production runs found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Run No</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Machine</TableHead>
                    <TableHead>Shift</TableHead>
                    <TableHead>Parts</TableHead>
                    <TableHead className="text-right">Produced</TableHead>
                    <TableHead className="text-right">Accepted</TableHead>
                    <TableHead className="text-right">Rejected</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.flatMap(item => {
                    const activeOutputs = (item.outputs || []).filter(o => parseFloat(o.produced_quantity) > 0);
                    const rows = activeOutputs.length > 0 ? activeOutputs : [null];
                    const numRows = rows.length;
                    return rows.map((o, i) => (
                      <TableRow key={`${item.id}-${i}`} className={i > 0 ? 'border-t border-slate-100' : ''}>
                        {i === 0 && (
                          <>
                            <TableCell className="font-mono font-bold align-top" rowSpan={numRows}>RUN-{item.run_no}</TableCell>
                            <TableCell className="align-top text-sm" rowSpan={numRows}>{new Date(item.run_date).toLocaleDateString()}</TableCell>
                            <TableCell className="align-top text-sm" rowSpan={numRows}>{getTemplateName(item.template_id)}</TableCell>
                            <TableCell className="align-top text-sm" rowSpan={numRows}>{item.customer_id ? getCustomerName(item.customer_id) : '-'}</TableCell>
                            <TableCell className="align-top text-sm" rowSpan={numRows}>{item.machine_id ? getMachineName(item.machine_id) : '-'}</TableCell>
                            <TableCell className="align-top text-sm" rowSpan={numRows}>{item.shift_id ? getShiftName(item.shift_id) : '-'}</TableCell>
                          </>
                        )}
                        <TableCell className="text-sm">{o ? getPartLabel(o.part_id) : '-'}</TableCell>
                        <TableCell className="text-right font-mono">{o ? parseFloat(o.produced_quantity).toFixed(0) : '-'}</TableCell>
                        <TableCell className="text-right font-mono text-green-600">{o ? parseFloat(o.accepted_quantity).toFixed(0) : '-'}</TableCell>
                        <TableCell className="text-right font-mono text-red-600">{o ? parseFloat(o.rejected_quantity).toFixed(0) : '-'}</TableCell>
                        {i === 0 && (
                          <>
                            <TableCell className="align-top" rowSpan={numRows}>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[item.status] || 'bg-slate-100 text-slate-600'}`}>
                                {item.status}
                              </span>
                            </TableCell>
                            <TableCell className="text-right align-top" rowSpan={numRows}>
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="icon" onClick={() => handleView(item)} className="h-8 w-8">
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {canEdit && (
                                  <>
                                    <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(item)} className="h-8 w-8">
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={() => handleDelete(item)} className="h-8 w-8 text-red-600">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ));
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? `Edit RUN-${editingItem.run_no}` : 'New Production Run'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            {/* Run Header */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4">
              <div className="col-span-2">
                <Label className="form-label">Template <span className="text-red-500">*</span></Label>
                <Select value={formData.template_id} onValueChange={handleTemplateChange}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select Template" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id.toString()}>{t.template_no} - {t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Date <span className="text-red-500">*</span></Label>
                <Input type="date" value={formData.run_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, run_date: e.target.value }))}
                  className="rounded-sm" />
              </div>
              <div>
                <Label className="form-label">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DRAFT">Draft</SelectItem>
                    <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                    <SelectItem value="COMPLETED">Completed</SelectItem>
                    <SelectItem value="CANCELLED">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Customer</Label>
                <Select value={formData.customer_id} onValueChange={(v) => setFormData(prev => ({ ...prev, customer_id: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select Customer" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id.toString()}>{c.code} - {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Machine</Label>
                <Select value={formData.machine_id} onValueChange={(v) => setFormData(prev => ({ ...prev, machine_id: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select Machine" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {machines.map(m => (
                      <SelectItem key={m.id} value={m.id.toString()}>{m.code} - {m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Shift</Label>
                <Select value={formData.shift_id} onValueChange={(v) => setFormData(prev => ({ ...prev, shift_id: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select Shift" /></SelectTrigger>
                  <SelectContent>
                    {shifts.map(s => (
                      <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Shift Incharge</Label>
                <Select value={formData.shift_incharge_id} onValueChange={(v) => setFormData(prev => ({ ...prev, shift_incharge_id: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select Incharge" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {employees.map(e => (
                      <SelectItem key={e.id} value={e.id.toString()}>{e.employee_code} - {e.first_name} {e.last_name || ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Operator</Label>
                <Select value={formData.operator_id} onValueChange={(v) => setFormData(prev => ({ ...prev, operator_id: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select Operator" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {employees.map(e => (
                      <SelectItem key={e.id} value={e.id.toString()}>{e.employee_code} - {e.first_name} {e.last_name || ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">No. of Shots</Label>
                <Input type="number" value={formData.no_of_shots}
                  onChange={(e) => handleShotsChange(e.target.value)} className="rounded-sm" />
              </div>
              <div>
                <Label className="form-label">Lumps (kg)</Label>
                <Input type="number" step="0.001" value={formData.lumps}
                  onChange={(e) => setFormData(prev => ({ ...prev, lumps: e.target.value }))} className="rounded-sm" />
              </div>
              <div>
                <Label className="form-label">Runner Weight (g) (auto)</Label>
                <Input type="number" step="0.0001" value={formData.runner_weight_total}
                  onChange={(e) => setFormData(prev => ({ ...prev, runner_weight_total: e.target.value }))}
                  className="rounded-sm bg-slate-50" />
              </div>
              <div className="col-span-2 md:col-span-4">
                <Label className="form-label">Notes</Label>
                <Input value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes" className="rounded-sm" />
              </div>
            </div>

            {/* Output Section */}
            {outputs.length > 0 && (
              <div className="border-t pt-4 mt-2">
                <h3 className="font-semibold text-slate-900 mb-3">Part Output</h3>
                <div className="space-y-4">
                  {outputs.map((o, i) => (
                    <div key={i} className="border rounded-sm p-3 bg-slate-50/50">
                      {/* Part header row */}
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-semibold text-sm text-slate-800">{getPartLabel(o.part_id)}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>Accepted: <span className="font-mono font-semibold text-green-700">{parseFloat(o.accepted_quantity).toFixed(0)}</span></span>
                          <span>Rejected: <span className="font-mono font-semibold text-red-600">{parseFloat(o.rejected_quantity).toFixed(0)}</span></span>
                          <span>Rej.Wt: <span className="font-mono text-red-500">{parseFloat(o.rejection_weight).toFixed(2)}g</span></span>
                        </div>
                      </div>

                      {/* Produced qty */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <div>
                          <Label className="text-xs text-slate-500">Produced</Label>
                          <Input type="number" value={o.produced_quantity}
                            onChange={(e) => handleProducedChange(i, e.target.value)}
                            className="rounded-sm" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Accepted (auto)</Label>
                          <Input type="number" value={o.accepted_quantity} readOnly
                            className="rounded-sm bg-white text-green-700 font-semibold" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Total Rejected (auto)</Label>
                          <Input type="number" value={o.rejected_quantity} readOnly
                            className="rounded-sm bg-white text-red-600 font-semibold" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Total Rej. Weight g (auto)</Label>
                          <Input type="number" value={o.rejection_weight} readOnly
                            className="rounded-sm bg-white text-red-500" />
                        </div>
                      </div>

                      {/* Rejection rows */}
                      <div className="border-t pt-2">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Rejections</span>
                          <Button type="button" variant="outline" size="sm" onClick={() => addRejection(i)}
                            className="h-7 text-xs rounded-sm">
                            <Plus className="w-3 h-3 mr-1" /> Add Rejection
                          </Button>
                        </div>
                        {o.rejections.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">No rejections recorded.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {o.rejections.map((r, j) => (
                              <div key={j} className="grid grid-cols-3 gap-2 items-center">
                                <div>
                                  <Select value={r.reason_for_rejection || ''}
                                    onValueChange={(v) => updateRejection(i, j, 'reason_for_rejection', v)}>
                                    <SelectTrigger className="rounded-sm h-8 text-xs"><SelectValue placeholder="Reason" /></SelectTrigger>
                                    <SelectContent className="max-h-60">
                                      {REJECTION_REASONS.map(rr => (
                                        <SelectItem key={rr} value={rr}>{rr}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Input type="number" placeholder="Qty" value={r.rejected_quantity}
                                    onChange={(e) => updateRejection(i, j, 'rejected_quantity', e.target.value)}
                                    className="rounded-sm h-8 text-sm" />
                                </div>
                                <div className="flex items-center gap-1">
                                  <div className="flex-1">
                                    <Input type="number" placeholder="Wt (g, auto)" value={r.rejection_weight} readOnly
                                      className="rounded-sm h-8 text-sm bg-white text-red-500" />
                                  </div>
                                  <Button type="button" variant="ghost" size="icon"
                                    onClick={() => removeRejection(i, j)} className="h-8 w-8 text-red-500 shrink-0">
                                    <X className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Material Consumption Section */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Material Consumption</h3>
                <Button type="button" variant="outline" size="sm" onClick={addMaterialRow} className="rounded-sm">
                  <Plus className="w-3 h-3 mr-1" /> Add Row
                </Button>
              </div>
              {materialConsumptions.length === 0 ? (
                <p className="text-sm text-slate-400 mb-3">No material rows. Select a template with materials or add rows manually.</p>
              ) : (
                <div className="space-y-2">
                  {materialConsumptions.map((mc, i) => (
                    <div key={i} className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
                      <div className="col-span-2">
                        <Label className="text-xs text-slate-500">Material</Label>
                        <Select value={mc.item_id} onValueChange={(v) => updateMaterial(i, 'item_id', v)}>
                          <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="Select Material" /></SelectTrigger>
                          <SelectContent className="max-h-60">
                            {templateMaterialItems.map(it => (
                              <SelectItem key={it.id} value={it.id.toString()}>{it.code} - {it.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">GRN</Label>
                        <Select value={mc.purchase_id} onValueChange={(v) => updateMaterial(i, 'purchase_id', v)}
                          disabled={!mc.item_id}>
                          <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="GRN" /></SelectTrigger>
                          <SelectContent className="max-h-60">
                            {(grnCache[mc.item_id] || []).map(g => (
                              <SelectItem key={g.id} value={g.id.toString()}>GRN-{g.grn_no}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Issue No</Label>
                        <Select value={mc.issue_item_no} onValueChange={(v) => updateMaterial(i, 'issue_item_no', v)}
                          disabled={!mc.purchase_id}>
                          <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="Issue No" /></SelectTrigger>
                          <SelectContent className="max-h-60">
                            {(issueCache[`${mc.purchase_id}_${mc.item_id}`] || []).map(iss => (
                              <SelectItem key={iss.id} value={iss.issue_item_no}>
                                {iss.issue_item_no} ({iss.quantity})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Qty Consumed</Label>
                        <Input type="number" step="0.001" value={mc.quantity_consumed}
                          onChange={(e) => updateMaterial(i, 'quantity_consumed', e.target.value)}
                          className="rounded-sm h-9" />
                      </div>
                      <div className="flex items-end gap-1">
                        <div className="flex-1">
                          <Label className="text-xs text-slate-500">UOM</Label>
                          <Input value={mc.uom}
                            onChange={(e) => updateMaterial(i, 'uom', e.target.value)}
                            placeholder="kg" className="rounded-sm h-9" />
                        </div>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeMaterialRow(i)} className="h-9 w-9 text-red-500">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" disabled={isSubmitting} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                {isSubmitting ? 'Saving...' : editingItem ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>RUN-{viewingRun?.run_no}</DialogTitle>
          </DialogHeader>
          {viewingRun && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div><span className="text-sm text-slate-500">Template</span><p className="font-medium">{getTemplateName(viewingRun.template_id)}</p></div>
                <div><span className="text-sm text-slate-500">Date</span><p className="font-medium">{new Date(viewingRun.run_date).toLocaleDateString()}</p></div>
                <div><span className="text-sm text-slate-500">Status</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[viewingRun.status] || ''}`}>{viewingRun.status}</span>
                </div>
                <div><span className="text-sm text-slate-500">Customer</span><p className="font-medium">{viewingRun.customer_id ? getCustomerName(viewingRun.customer_id) : '-'}</p></div>
                <div><span className="text-sm text-slate-500">Machine</span><p className="font-medium">{viewingRun.machine_id ? getMachineName(viewingRun.machine_id) : '-'}</p></div>
                <div><span className="text-sm text-slate-500">Shift</span><p className="font-medium">{viewingRun.shift_id ? getShiftName(viewingRun.shift_id) : '-'}</p></div>
                <div><span className="text-sm text-slate-500">Shift Incharge</span><p className="font-medium">{viewingRun.shift_incharge_id ? getEmpName(viewingRun.shift_incharge_id) : '-'}</p></div>
                <div><span className="text-sm text-slate-500">Operator</span><p className="font-medium">{viewingRun.operator_id ? getEmpName(viewingRun.operator_id) : '-'}</p></div>
                <div><span className="text-sm text-slate-500">Shots</span><p className="font-mono font-bold">{viewingRun.no_of_shots}</p></div>
                <div><span className="text-sm text-slate-500">Runner Weight (g)</span><p className="font-mono">{parseFloat(viewingRun.runner_weight_total || 0).toFixed(2)}</p></div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Part Outputs</h4>
                {(viewingRun.outputs || []).map((o, i) => (
                  <div key={i} className="border rounded-sm p-3 mb-2">
                    <p className="font-medium text-sm mb-2">{getPartLabel(o.part_id)}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-2">
                      <div>Produced: <span className="font-mono">{parseFloat(o.produced_quantity).toFixed(0)}</span></div>
                      <div className="text-green-700">Accepted: <span className="font-mono">{parseFloat(o.accepted_quantity).toFixed(0)}</span></div>
                      <div className="text-red-600">Rejected: <span className="font-mono">{parseFloat(o.rejected_quantity).toFixed(0)}</span></div>
                      <div className="text-red-500">Rej. Weight: <span className="font-mono">{parseFloat(o.rejection_weight || 0).toFixed(2)}g</span></div>
                    </div>
                    {(o.rejections || []).length > 0 && (
                      <div className="text-xs border-t pt-2 space-y-1">
                        {o.rejections.map((r, j) => (
                          <div key={j} className="flex gap-4 text-slate-600">
                            <span className="font-medium">{r.reason_for_rejection || '-'}</span>
                            <span>Qty: <span className="font-mono">{parseFloat(r.rejected_quantity).toFixed(0)}</span></span>
                            <span>Wt: <span className="font-mono">{parseFloat(r.rejection_weight || 0).toFixed(2)}g</span></span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {(viewingRun.material_consumptions || []).length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Material Consumption</h4>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Material</TableHead>
                        <TableHead>GRN</TableHead>
                        <TableHead>Issue No</TableHead>
                        <TableHead className="text-right">Qty Consumed</TableHead>
                        <TableHead>UOM</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewingRun.material_consumptions.map((mc, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm">{getLabel(items, mc.item_id, it => `${it.code} - ${it.name}`)}</TableCell>
                          <TableCell className="font-mono text-sm">{mc.purchase_id ? getGRNLabel(mc.purchase_id) : '-'}</TableCell>
                          <TableCell className="text-sm">{mc.issue_item_no || '-'}</TableCell>
                          <TableCell className="text-right font-mono">{parseFloat(mc.quantity_consumed).toFixed(3)}</TableCell>
                          <TableCell className="text-sm">{mc.uom || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductionRun;
